import { HttpClient } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import {
    NavigationCancel,
    NavigationEnd,
    NavigationStart,
    Router,
} from "@angular/router";
import { BehaviorSubject, merge, Observable, of, zip } from "rxjs";
import {
    catchError,
    concatMap,
    delay,
    filter,
    map,
    switchMap,
    tap,
} from "rxjs/operators";
import { PagedResults, PagerOptions } from "../../app.types";
import { stringifyPagerOptions } from "../../../util/http-helpers";
import { ConfigService } from "../config/config.service";
import { BaseAuditLogService } from "node_modules/@synx-cloud/services";

export interface IAuditLog {
    itemId?: string;
    type?: string;
    fieldName: string;
    action: string;
    previousValue: string;
    newValue?: any;
    dateTime?: Date;
    trackingDescription: string;
    userId?: string;
    user?: string;
    caption?: string;
    activity?: string;
    entityFriendlyName?: string;
    previousValueFriendlyName?: string;
    newValueFriendlyName?: string;
    redirection?: { link?: string; text?: string };
}

/**
 * Subset of audit log actions (ChangeLogEnum names) the device management UI
 * generates captions for. The backend serialises the action as the enum name.
 */
export enum AuditLogAction {
    ADD = "Add",
    EDIT = "Edit",
    DELETE = "Delete",
    LOGIN = "Login",
    LOGOUT = "Logout",
    VIEW = "View",
    EXPORT = "Export",
}

/**
 * View model consumed by the audit-logging-table component. Built by mapping
 * the raw IAuditLog response items into this richer, display-ready shape.
 */
export interface AuditLog {
    id: string;
    date: Date;
    user: string;
    userId: string;
    activity: {
        id: string;
        activity: string;
        color: string;
        caption: string;
        redirection?: {
            link?: string;
            text: string;
            duration?: string;
        };
    };
}

/**
 * Calculate time difference in minutes
 */
function calcDuration(startTime: Date, endTime: Date) {
    const timeDiff = endTime.getTime() - startTime.getTime();
    return Math.round((timeDiff / (1000 * 60)) * 100) / 100;
}

/**
 * Audit log service watches router for navigation events and provides
 * a method for modal components to log open events.
 * Posts audit information to the DeviceManagementAuditLogs endpoint.
 */
@Injectable({ providedIn: "root" })
export class AuditLogService extends BaseAuditLogService {

    FRIENDLY_FIELD_NAMES = {
        "CustomerTypeId": "Customer Type",
        "CustomerInstanceUrlId": "Customer Instance URL",
        "AssetTypeId": "Asset Type",
        "AssetGroupPath": "Asset Group Path",
        "EnableRecordingMapRequest": "Enable Recording Map Request",
        "EnableCamerasSnapshotRequest": "Enable Cameras Snapshot Request",
        "FleetId": "Fleet ID",
        "APIKey": "API Key",
        "CustomerId": "Customer",
        "PONumber": "PO Number",
        "SubscriptionEndDate": "Subscription End Date",
        "SubscriptionStartDate": "Subscription Start Date",
        "IntegratorName": "Integrator Name",
        "LiveVideoLimit": "Live Video Limit",
        "AssetLimit": "Asset Limit",
        "IncidentCreationLimit": "Incident Creation Limit",
        "ConcurrentUsersLimit": "Concurrent Users Limit",
        "RedactionLimit": "Redaction Limit",
        "StorageLimit": "Storage Limit",
        "CloudInstanceUrl": "Cloud Instance URL",
        "IntegratorContact": "Integrator Contact",
        "CreatedDate": "Created Date",
        "RedactionEnabled": "Redaction Enabled",
        "RedactionTrialStartedAt": "Redaction Trial Start",
        "RedactionTrialExpiresAt": "Redaction Trial Expiry",
        "TenantId": "Tenant ID",
    };

    ACTIVITY_IDS = {
        DEVICE_MANAGEMENT: "DeviceManagement",
        CUSTOMER_MANAGEMENT: "CustomerManagement",
        ADMIN: "Admin",
        NAVIGATION: "Navigation",
    };

    ACTIVITY_TITLES = {
        DeviceManagement: "Device Management",
        CustomerManagement: "Customer Management",
        Admin: "Admin",
        Navigation: "Navigation"
    };

    ACTIVITIES = [
        {
            name: "Device Management",
            value: this.ACTIVITY_IDS.DEVICE_MANAGEMENT,
            color: "#AE6013",
        },
        { 
            name: "Customer Management", 
            value: this.ACTIVITY_IDS.CUSTOMER_MANAGEMENT, 
            color: "rgb(18, 125, 171)" },
        { 
            name: "Admin", 
            value: this.ACTIVITY_IDS.ADMIN, 
            color: "#B75353" },
        {
            name: "Navigation",
            value: this.ACTIVITY_IDS.NAVIGATION,
            color: "#A44C7E",
        },
    ];

    /**
     * Whether logs generated by cloud processes (i.e. with no resolved user)
     * are shown in the audit-logging-table. Held here so the value persists
     * for the lifetime of the page.
     */
    cloudProcessEnabled = false;

    getActivityColor(id: string): string {
        const activity = this.ACTIVITIES.find((a) => a.value === id);
        return activity ? activity.color : "#000000";
    }

    /**
     * Resolves a navigation url to a friendlier activity label. Device
     * management audit logs do not currently carry redirection urls, so this
     * returns an empty string (matching the "no match" behaviour in CEL).
     */
    matchUrlWithDetailedActivity(rawUrl: string): string {
        return "";
    }

    /**
     * Produces a human-readable caption for a log entry. Ported from CEL,
     * scoped to the activities device management actually emits: Admin
     * (login/logout) and Navigation (page duration / modal / route changes).
     * Everything else - including the DeviceManagement activity, whose caption
     * is generated server-side - falls back to the stored caption.
     */
    captionGenerator(log: IAuditLog): string {
        if (log.activity === this.ACTIVITY_IDS.ADMIN) {
            if (log.action === AuditLogAction.LOGIN) {
                return "Logged In";
            }
            if (log.action === AuditLogAction.LOGOUT) {
                return "Logged Out";
            }
            if (log.action === AuditLogAction.EXPORT) {
                return log.newValue;
            }
        } else if (log.activity === this.ACTIVITY_IDS.NAVIGATION) {
            if (log.fieldName === "PageDuration") {
                const duration = JSON.parse(log.newValue)?.duration;
                return "Page Duration: " + duration + " minutes";
            }

            if (log.type === "Modal") {
                const vehicleName = JSON.parse(log.newValue)?.key;
                const cameraName = JSON.parse(log.newValue)?.dataId;
                return (
                    "Viewed " +
                    log.trackingDescription +
                    (vehicleName && cameraName
                        ? " | " + vehicleName + " - " + cameraName
                        : "")
                );
            }
            return "Navigated to";
        } else if (log.activity === this.ACTIVITY_IDS.DEVICE_MANAGEMENT || log.activity === this.ACTIVITY_IDS.CUSTOMER_MANAGEMENT) {
            // IoT Tool operations tag the entry with a marker field so each reads as what it did.
            // File ops have no device, so they append the file name; device ops are a plain phrase
            // because the entry already carries a device link.
            const fileCaptions: { [key: string]: string } = {
                FirmwareFileUpload: "Uploaded firmware",
                FirmwareFileDelete: "Deleted firmware",
                ConfigurationFileUpload: "Uploaded configuration",
            };
            const deviceCaptions: { [key: string]: string } = {
                DeviceRevertDefaults: "Reverted device to defaults",
                DeviceSimulateFailure: "Simulated failure on device",
                DeviceShellCommand: "Ran shell command on device",
                DeviceGpsBatchSize: "Set GPS batch size on device",
                DeviceTeltonikaConfig: "Sent Teltonika configuration to device",
                DeviceConfigPush: "Pushed configuration to device",
                DeviceConfigPull: "Pulled configuration from device",
                DeviceMoveEnvironment: "Moved device to another environment",
                DeviceKeysRevealed: "Revealed device keys",
                DeviceFirmwareStatus: "Viewed firmware update status",
                DeviceLogsDownload: "Downloaded device logs",
            };
            if (fileCaptions[log.fieldName]) {
                return `${fileCaptions[log.fieldName]} ${log.entityFriendlyName ?? log.newValue}`;
            }
            if (deviceCaptions[log.fieldName]) {
                // The shell command includes the command text that was sent, so it's auditable.
                if (log.fieldName === "DeviceShellCommand") {
                    const command = this.extractCommandSent(log.newValue);
                    return command ? `${deviceCaptions[log.fieldName]}: ${command}` : deviceCaptions[log.fieldName];
                }
                return deviceCaptions[log.fieldName];
            }
            if (log.action === AuditLogAction.VIEW) {
                return `Viewed ${log.type} ${log.entityFriendlyName ?? log.itemId}`;
            } else if (log.action === AuditLogAction.EDIT) {
                return (
                    `Edited ${this.FRIENDLY_FIELD_NAMES[log.fieldName] || log.fieldName} to ` +
                    (log.newValueFriendlyName ?? log.newValue)
                );
            } else if (log.action === AuditLogAction.ADD) {
                return `Created ${log.type} ${log.entityFriendlyName ?? log.itemId}`;
            }
        }

        return log.caption;
    }

    /**
     * Pulls the command text out of a shell-command audit's NewValue envelope
     * ({ ..., Details: { Command } }), so it can be shown in the caption. Returns null if absent.
     */
    private extractCommandSent(newValue: string): string | null {
        if (!newValue) { return null; }
        try {
            const parsed = JSON.parse(newValue);
            const command = parsed?.Details?.Command ?? parsed?.Command;
            return typeof command === "string" && command.trim() ? command : null;
        } catch {
            return null;
        }
    }

    /**
     * Builds the redirection descriptor (link + display text) for a log entry.
     * Navigation redirections are resolved here from the stored url. All other
     * redirections (device/customer management) are resolved server-side and
     * arrive on the log's `redirection` property.
     */
    getLogRedirectionUrl(log: IAuditLog): { link?: string; text: string } | undefined {
        if (log.activity === this.ACTIVITY_IDS.NAVIGATION) {
            const url =
                log.fieldName === "PageDuration"
                    ? JSON.parse(log.newValue)?.url
                    : JSON.parse(log.newValue)?.urlAfterRedirects;
            return {
                link: url,
                text: url,
            };
        }

        if (log.redirection?.text) {
            return {
                link: log.redirection.link,
                text: log.redirection.text,
            };
        }

        return undefined;
    }

    /**
     * Opens the redirection target for a log entry. Device management logs only
     * redirect to navigation urls, so the link is opened in a new tab.
     */
    openUrl(log: AuditLog): void {
        if (log.activity.redirection?.link) {
            window.open(log.activity.redirection.link, "_blank");
        }
    }

    get baseUrl() {
        return `${this.configService.API_URI}/DeviceManagementAuditLogs`;
    }

    logEvt$(payload: IAuditLog) {
        return this.httpClient.post(this.baseUrl, {
            ...payload,
            dateTime: new Date(),
        });
    }

    searchUserAuditLogs(
        opts: PagerOptions,
        userIds: string[],
        startDate: Date,
        endDate: Date
    ): Observable<PagedResults<IAuditLog>> {
        let url = `${this.baseUrl}/user${stringifyPagerOptions(opts)}`;

        if (userIds?.length) {
            url += "&" + userIds.map(id => `userIds=${encodeURIComponent(id)}`).join("&");
        }
        if (startDate) {
            url += `&startDate=${encodeURIComponent(startDate.toISOString())}`;
        }
        if (endDate) {
            url += `&endDate=${encodeURIComponent(endDate.toISOString())}`;
        }

        return this.httpClient.get<PagedResults<IAuditLog>>(url);
    }

    constructor(
        private readonly router: Router,
        private readonly configService: ConfigService,
        httpClient: HttpClient,
        readonly ngZone: NgZone
    ) {
        super(httpClient);
        
        ngZone.runOutsideAngular(() => {
            const next$ = new BehaviorSubject<string>("start");
            next$
                .pipe(
                    //tap((e) => console.log('Audit:', e, typeof this.configService.API_URI) ),
                    switchMap(() =>
                        this.configService.API_URI === undefined
                            ? of("no base url")
                            : this.httpClient
                                  .get(
                                      this.configService.CEL_API_URI +
                                          "/usage/devicemanagementsession/ping"
                                  )
                                  .pipe(catchError((err) => of(err)))
                    ),
                    //tap(res => console.log('Audit:res', res)),
                    delay(10000)
                )
                .subscribe((res) => next$.next("trigger"));
        });
        this.startLoggingNavigation();
    }

    /**
     * Called by ModalComponent when a modal is opened.
     * @param modalId The modal identifier from the modalId input
     */
    logModalOpen(modalId: string) {
        this.logEvt$(<IAuditLog>{
            fieldName: "Open Modal",
            type: "Modal",
            previousValue: null,
            newValue: JSON.stringify({ key: modalId }),
            action: "Navigation",
            trackingDescription: modalId
                .replace(/-/g, " ")
                .replace(/\b([a-z])/g, (match) => match.toUpperCase()),
        })
            .pipe(catchError((err) => of(err)))
            .subscribe(() => {});
    }

    _previousValue: string = null;
    _lastNav = new Date();

    private startLoggingNavigation() {
        const requestedRouteEvts$ = this.router.events.pipe(
            filter((evt) => evt instanceof NavigationStart)
        );
        const successfulRouteEvts$ = this.router.events.pipe(
            filter((evt) => evt instanceof NavigationEnd)
        );
        const failedRoutesEvts$ = this.router.events.pipe(
            filter((evt) => evt instanceof NavigationCancel)
        );
        const allFinalEvts$ = merge(successfulRouteEvts$, failedRoutesEvts$);

        const aggregatedRouteEvts$ = zip(
            requestedRouteEvts$,
            allFinalEvts$,
        );

        aggregatedRouteEvts$
            .pipe(
                map(
                    ([startEvt, endEvt]: [
                        NavigationStart,
                        NavigationEnd | NavigationCancel
                    ]) => {
                        let message = "";
                        let urlAfterRedirects = "";
                        const isCancel = endEvt instanceof NavigationCancel;
                        if (isCancel) {
                            message = (endEvt as NavigationCancel).reason || "Cancelled";
                        } else {
                            urlAfterRedirects = (endEvt as NavigationEnd).urlAfterRedirects;
                        }

                        const data: any = {
                            success: !isCancel,
                            url: startEvt.url,
                            urlAfterRedirects: urlAfterRedirects,
                            failedReason: message,
                        };

                        return [
                            <IAuditLog>{
                                fieldName: "Navigation",
                                type: "Link",
                                previousValue: this._previousValue,
                                newValue: JSON.stringify(data),
                                action: "Navigation",
                                trackingDescription: data.success
                                    ? "Navigation Successful"
                                    : "Navigation Failed",
                            },
                            this._previousValue !== null
                                ? <IAuditLog>{
                                      fieldName: "PageDuration",
                                      type: "Link",
                                      previousValue: null,
                                      newValue: JSON.stringify({
                                          url: JSON.parse(this._previousValue)
                                              .url,
                                          duration: calcDuration(
                                              this._lastNav,
                                              new Date()
                                          ),
                                          ...data,
                                      }),
                                      action: "Navigation",
                                      trackingDescription: "Navigation Away",
                                  }
                                : null,
                        ];
                    }
                ),
                tap((payload) => {
                    if (
                        payload[0].trackingDescription ===
                        "Navigation Successful"
                    ) {
                        this._previousValue = payload[0].newValue;
                        this._lastNav = new Date();
                    } else {
                        this._previousValue = null;
                    }
                }),
                concatMap((evts) =>
                    zip(
                        ...evts
                            .filter((evt) => evt !== null)
                            .map((evt) => this.logEvt$(evt))
                    )
                )
            )
            .subscribe();
    }

    public logCloseApplication(): Observable<any> {
        const data: any = {};

        return this.logEvt$(<IAuditLog>{
            fieldName: "PageDuration",
            type: "Link",
            previousValue: null,
            newValue: JSON.stringify({
                url: JSON.parse(this._previousValue).url,
                duration: calcDuration(this._lastNav, new Date()),
                ...data,
            }),
            action: "Navigation",
            trackingDescription: "Navigation Close",
        });
    }
}
